/**
 * AuthGuard — componente de proteção de rotas.
 *
 * Redireciona para /login se o usuário não estiver autenticado.
 * Exibe um spinner Nava enquanto a sessão está sendo restaurada (status=idle|loading).
 *
 * Uso em AppRouter:
 *   <Route path="/dashboard" element={<AuthGuard><AppShell ...><DashboardScreen /></AppShell></AuthGuard>} />
 *
 * MFA Admin: ao detectar status='mfa-required', renderiza MfaScreen em vez
 * de redirecionar para /login.
 *
 * Acessibilidade: o spinner tem aria-label; o redirect usa <Navigate replace>
 * para não poluir o histórico de navegação.
 */
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { MfaScreen } from './MfaScreen';
import { C } from '@/theme/palette';

/** Spinner mínimo enquanto a sessão está sendo restaurada. */
function SessionRestoring() {
  return (
    <div
      role="status"
      aria-label="Verificando sessão..."
      style={{
        minHeight: '100vh',
        backgroundColor: C.black,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        aria-hidden="true"
        style={{
          width: 40,
          height: 40,
          borderRadius: '50%',
          border: `3px solid ${C.blackBorder}`,
          borderTopColor: C.cyber,
          animation: 'nava-spin 0.8s linear infinite',
        }}
      />
      <style>{`
        @media (prefers-reduced-motion: no-preference) {
          @keyframes nava-spin { to { transform: rotate(360deg); } }
        }
        @media (prefers-reduced-motion: reduce) {
          /* Remove animação; mantém visual estático */
          [aria-label="Verificando sessão..."] div { border-top-color: ${C.cyber}; animation: none; }
        }
      `}</style>
    </div>
  );
}

/**
 * AuthGuard — protege as rotas autenticadas.
 *
 * @param {{ children: React.ReactNode, requireAdmin?: boolean }} props
 *   requireAdmin: se true, exige matrix_id === 'm-admin' (além de estar autenticado).
 */
export function AuthGuard({ children, requireAdmin = false }) {
  const { status, user } = useAuth();
  const location = useLocation();

  // Sessão sendo restaurada (F5 com refresh token válido) → exibe spinner
  if (status === 'idle' || status === 'loading') {
    return <SessionRestoring />;
  }

  // MFA obrigatório para Admin — exibe tela de MFA (não redireciona para login)
  if (status === 'mfa-required') {
    return <MfaScreen />;
  }

  // Não autenticado → redireciona para login preservando destino original (state.from)
  if (status !== 'authenticated') {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Acesso Admin exigido mas usuário não é Admin → 403 implícito, volta ao dashboard
  if (requireAdmin && user?.matrix_id !== 'm-admin') {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}
